import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { twMerge } from "tailwind-merge";

interface Props {
  field: any;
}

export default function ToolRegulatoryQuestionnaireFormField({ field }: Props) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const input = useMemo(() => {
    if (field.fieldType === "text" || field.fieldType === "phonenumber") {
      if (field.name === "email") {
        return (
          <input
            placeholder={`Input ${field.label}`}
            className={twMerge(
              "px-[12px] py-[6px] border-2 border-solid border-[#ddd] w-full !focus:outline-none !ring-0 focus:border-smart-teal"
            )}
            type="email"
            {...register(field.name, { required: field.required })}
          />
        );
      }

      return (
        <input
          placeholder={`Input ${field.label}`}
          className={twMerge(
            "px-[12px] py-[6px] border-2 border-solid border-[#ddd] w-full !focus:outline-none !ring-0 focus:border-smart-teal"
          )}
          type={field.fieldType}
          {...register(field.name, { required: field.required })}
        />
      );
    }

    if (field.fieldType === "radio") {
      return field.options.map((item: any, index: number) => {
        return (
          <label
            className="flex items-center gap-2"
            key={`${field.name}-option-${index}`}
          >
            <input
              placeholder={`Input ${field.label}`}
              className="shadow-inner border border-solid border-[#ddd]"
              value={item.value}
              type="radio"
              {...register(field.name, { required: field.required })}
            />
            <span>{item?.label}</span>
          </label>
        );
      });
    }

    if (field.fieldType === "checkbox") {
      return field.options.map((item: any, index: number) => {
        return (
          <label
            className="flex items-center gap-2"
            key={`${field.name}-option-${index}`}
          >
            <input
              placeholder={`Input ${field.label}`}
              className="shadow-inner border border-solid border-[#ddd] rounded-none"
              value={item.value}
              type="checkbox"
              {...register(field.name, { required: field.required })}
            />
            <span>{item?.label}</span>
          </label>
        );
      });
    }

    if (field.fieldType === "textarea") {
      return (
        <textarea
          className="px-[12px] py-[6px] shadow-inner border border-solid border-[#ddd] w-full"
          rows={3}
          {...register(field.name, { required: field.required })}
        ></textarea>
      );
    }

    if (field.fieldType === "select") {
      return (
        <select
          className="px-[12px] py-[6px] shadow-inner border border-solid border-[#ddd] w-full"
          {...register(field.name, { required: field.required })}
        >
          {field.options.map((item: any, index: number) => {
            return (
              <option key={`${field.name}-option-${index}`} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </select>
      );
    }
  }, [
    field.fieldType,
    field.label,
    field.name,
    field.options,
    field.required,
    register,
  ]);

  const dependentFields = useMemo(() => {
    if (!field?.dependentFieldFilters?.length) return;

    return field?.dependentFieldFilters?.map((item: any, index: number) => {
      return (
        <div className="mt-4" key={`${field.name}-dependent-${index}`}>
          <ToolRegulatoryQuestionnaireFormField
            field={item.dependentFormField}
          />
        </div>
      );
    });
  }, [field?.dependentFieldFilters, field?.name]);

  return (
    <div className="mb-4 flex flex-col">
      <label
        htmlFor={field.name}
        className={twMerge(errors[field.name] && "text-red-500")}
      >
        {field.label}
        {field.required && (
          <span className="text-sm text-ink-pink ml-1">(required)</span>
        )}
      </label>
      <span className="text-xs mb-2">{field.description}</span>
      {input}
      {dependentFields}
    </div>
  );
}
